import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ExternalLink, Loader2, AlertTriangle, Search } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sales`;
const LABEL = { fontSize: 10.5, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#8A857A" };

const Row = ({ k, v }) => (
  <div className="flex items-baseline gap-2 py-1 border-b border-[#F4F2ED] last:border-0">
    <span style={{ ...LABEL, width: 110, flexShrink: 0 }}>{k}</span>
    <span className="text-[12.5px] break-all text-[#1A1A1A]">{v || "—"}</span>
  </div>
);

/* Confirm the HubSpot company before pulling anything: a wrong match would
   write another company's history onto this prospect. */
export const ProspectHubspotSyncDialog = ({ prospect, onClose, onSynced }) => {
  const [state, setState] = useState({ loading: true });
  const [chosen, setChosen] = useState(null);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const look = useCallback(async (q = "") => {
    setState({ loading: true });
    try {
      const r = await axios.get(`${API}/prospects/${encodeURIComponent(prospect.id)}/hubspot/match`,
        { params: q ? { q } : {} });
      setState({ loading: false, ...r.data });
      setChosen(r.data.match || null);
    } catch (e) {
      setState({ loading: false, error: e?.response?.data?.detail || "Couldn't reach HubSpot" });
    }
  }, [prospect.id]);

  useEffect(() => { look(""); }, [look]);

  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/prospects/${encodeURIComponent(prospect.id)}/hubspot/sync`,
        { company_id: chosen.id });
      setResult(r.data);
      const n = (r.data.activities?.imported || 0) + (r.data.records?.added || 0)
        + (r.data.contacts?.added || 0);
      toast.success(n ? `${n} new item${n === 1 ? "" : "s"} brought across` : "Nothing new in HubSpot");
      await onSynced();
    } catch (e) { toast.error(e?.response?.data?.detail || "The sync failed"); }
    finally { setBusy(false); }
  };

  const co = chosen;
  const candidates = state.candidates || [];

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="prospect-hubspot-dialog">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative h-full w-full max-w-[520px] flex flex-col bg-white border-l border-[#DAD5CA]">
        <div className="px-5 py-4 flex items-start justify-between gap-3 border-b border-[#F4F2ED]">
          <div>
            <div className="text-[17px] font-extrabold text-[#1A1A1A]">Sync with HubSpot</div>
            <div className="text-[12.5px] text-[#59554D]">
              Contacts, calls, meetings, emails, notes, deals and quotes logged against this company
              in HubSpot. Contacts already here are kept as they are, and nothing is duplicated —
              safe to run whenever something looks missing.
            </div>
          </div>
          <button onClick={onClose} data-testid="prospect-hubspot-close"
            className="w-8 h-8 rounded-[7px] flex items-center justify-center text-[#59554D] hover:bg-[#F6F4EF]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3.5">
          {state.loading && (
            <div className="flex items-center gap-2 text-[13px] text-[#59554D]" data-testid="prospect-hubspot-loading">
              <Loader2 className="w-4 h-4 animate-spin" /> Finding this company in HubSpot…
            </div>
          )}

          {state.error && (
            <div className="rounded-[8px] p-2.5 text-[12.5px] bg-[#FFF8D6] border border-[#E8C86A] text-[#6B5600]"
              data-testid="prospect-hubspot-error">{state.error}</div>
          )}

          {!state.loading && (
            <div>
              <div style={LABEL}>Search HubSpot by another name</div>
              <div className="flex gap-2 mt-1.5">
                <input value={query} onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") look(query.trim()); }}
                  placeholder={prospect.company_name || "Company name"}
                  data-testid="prospect-hubspot-search-input"
                  className="flex-1 h-9 px-2.5 rounded-[8px] bg-[#F4F2ED] border border-[#DAD5CA] text-[13px] text-[#1A1A1A] outline-none" />
                <button onClick={() => look(query.trim())} data-testid="prospect-hubspot-search-btn"
                  className="h-9 px-3 rounded-[8px] border border-[#DAD5CA] text-[12.5px] font-semibold text-[#59554D] hover:bg-[#F6F4EF] inline-flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5" /> Search
                </button>
              </div>
            </div>
          )}

          {!state.loading && !state.error && !co && (
            <div className="text-[13px] text-[#1A1A1A]" data-testid="prospect-hubspot-nomatch">
              Nothing in HubSpot matches <strong>{state.searched || prospect.company_name}</strong>.
              Try the name as it is spelled in HubSpot.
            </div>
          )}

          {co && (
            <div>
              <div style={LABEL}>The company we matched</div>
              <div className="mt-1.5 rounded-[8px] p-3 bg-[#FBFAF7] border border-[#E6E2D9]"
                data-testid="prospect-hubspot-match">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-[14px] font-bold text-[#1A1A1A]">{co.name}</div>
                  {co.hubspot_url && (
                    <a href={co.hubspot_url} target="_blank" rel="noopener noreferrer"
                      data-testid="prospect-hubspot-open"
                      className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#8A6D00]">
                      Check in HubSpot <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                <div className="mt-1.5">
                  <Row k="Domain" v={co.domain} />
                  <Row k="Location" v={[co.city, co.country].filter(Boolean).join(", ")} />
                  <Row k="Lifecycle" v={co.lifecycle_stage} />
                  <Row k="Owner" v={(co.owner || {}).full_name} />
                  {co.contact_count !== undefined && (
                    <Row k="Contacts" v={`${co.contact_count} linked contact${co.contact_count === 1 ? "" : "s"} — their activity comes across too`} />
                  )}
                </div>
              </div>
              {state.last_synced_at && (
                <div className="text-[11.5px] mt-1.5 text-[#59554D]" data-testid="prospect-hubspot-last">
                  Last synced {String(state.last_synced_at).slice(0, 10)}. Running it again only adds what's new.
                </div>
              )}
            </div>
          )}

          {candidates.length > 0 && (
            <div data-testid="prospect-hubspot-candidates">
              <div style={LABEL}>Not right? Pick another</div>
              <div className="mt-1.5 space-y-1">
                {candidates.map((c) => (
                  <button key={c.id} onClick={() => setChosen(c)} data-testid={`prospect-hubspot-candidate-${c.id}`}
                    className={`w-full text-left px-2.5 py-2 rounded-[8px] border text-[12.5px] ${chosen?.id === c.id
                      ? "border-[#1A1A1A] bg-[#F6F4EF]" : "border-[#E6E2D9] hover:bg-[#FBFAF7]"}`}>
                    <span className="font-semibold text-[#1A1A1A]">{c.name}</span>
                    {c.domain && <span className="text-[#8A857A]"> · {c.domain}</span>}
                    {c.city && <span className="text-[#8A857A]"> · {c.city}</span>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-[8px] p-3 space-y-1.5 bg-[#F6F4EF] border border-[#E6E2D9]"
              data-testid="prospect-hubspot-result">
              <div className="text-[13px] font-bold text-[#1A1A1A]">
                {result.contacts?.added || 0} contact{result.contacts?.added === 1 ? "" : "s"},{" "}
                {result.activities?.imported || 0} activit{result.activities?.imported === 1 ? "y" : "ies"},{" "}
                {result.records?.added || 0} deal/quote record{result.records?.added === 1 ? "" : "s"} added
              </div>
              {!!(result.activities?.already_there || result.contacts?.already_there) && (
                <div className="text-[12px] text-[#59554D]">
                  Already here, so left alone: {result.contacts?.already_there || 0} contacts,{" "}
                  {result.activities?.already_there || 0} activities.
                </div>
              )}
              {Object.keys(result.activities?.by_type || {}).length > 0 && (
                <div className="text-[12.5px] text-[#59554D]">
                  {Object.entries(result.activities.by_type)
                    .map(([k, n]) => `${n} ${k}${n === 1 ? "" : "s"}`).join(" · ")}
                </div>
              )}
              {!!(result.contacts?.names || []).length && (
                <div className="text-[11.5px] text-[#59554D]">
                  Added {result.contacts.names.join(", ")}
                </div>
              )}
              {!!(result.records?.added) && (
                <div className="text-[11.5px] text-[#59554D]">
                  Deals and quotes are recorded on the timeline as a note of what HubSpot holds —
                  they are not turned into EQUIPR quotes.
                </div>
              )}
              <div className="text-[11.5px] text-[#59554D]">
                HubSpot tasks were left out — they're someone's to-do, not contact with the customer,
                so counting them would flatter every last-touch date.
              </div>
              {(result.warnings || []).map((w, i) => (
                <div key={i} className="flex items-start gap-1.5 text-[12px] mt-1 text-[#6B5600]"
                  data-testid={`prospect-hubspot-warning-${i}`}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-3.5 flex items-center gap-2 bg-[#FBFAF7] border-t border-[#F4F2ED]">
          <button onClick={onClose} className="h-9 px-3 text-[13px] text-[#59554D] ml-auto"
            data-testid="prospect-hubspot-cancel">{result ? "Done" : "Cancel"}</button>
          {co && (
            <button onClick={run} disabled={busy} data-testid="prospect-hubspot-run"
              className="h-9 px-4 rounded-[7px] text-[12px] font-bold uppercase tracking-[0.04em] bg-[#FFD108] border border-[#E0B500] text-black disabled:opacity-60">
              {busy ? "Syncing…" : result ? "Run again" : "Sync from HubSpot"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProspectHubspotSyncDialog;
